import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  ObituaryDetailsData,
  obituaryDetailsService,
} from '../../../../services/obituaryDetailsInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('ObituaryDetailsData');

type ObituaryDetailsDataScreenRouteProp = RouteProp<RootStackParamList, 'ObituaryDetailsData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const ObituaryDetailsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<ObituaryDetailsDataScreenRouteProp>();
  const [obituaryDetailsInfo, setObituaryDetailsInfo] = useState<ObituaryDetailsData | null>(null);
  const [loading, setLoading] = useState(true);

  const obituaryDetailsId = route.params?.hasWriter;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadObituaryDetailsInfo();
  }, []);

  const loadObituaryDetailsInfo = async () => {
    try {
      // Load the first/latest entry if no specific ID provided
      const allInfo = await obituaryDetailsService.getAllObituaryDetails(targetUserId);
      if (allInfo.length > 0) {
        setObituaryDetailsInfo(allInfo[0]);
      }
    } catch (error) {
      logger.error('Error loading obituary details info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (obituaryDetailsInfo) {
      // Map the data from obituaryDetailsInfo to the format expected by ObituaryDetailsInputScreen
      const mappedData = {
        id: obituaryDetailsInfo.id,
        obituaryPreference: obituaryDetailsInfo.obituaryPreference,
        publicationNewspapers: obituaryDetailsInfo.publicationNewspapers,
        onlinePublications: obituaryDetailsInfo.onlinePublications,
        lengthPreference: obituaryDetailsInfo.lengthPreference,
        tonePreference: obituaryDetailsInfo.tonePreference,
        keyAccomplishments: obituaryDetailsInfo.keyAccomplishments,
        familyMentions: obituaryDetailsInfo.familyMentions,
        careerHighlights: obituaryDetailsInfo.careerHighlights,
        hobbiesInterests: obituaryDetailsInfo.hobbiesInterests,
        charitableWork: obituaryDetailsInfo.charitableWork,
        militaryService: obituaryDetailsInfo.militaryService,
        educationBackground: obituaryDetailsInfo.educationBackground,
        avoidTopics: obituaryDetailsInfo.avoidTopics,
        photoPreference: obituaryDetailsInfo.photoPreference,
        serviceInformation: obituaryDetailsInfo.serviceInformation,
        donationRequests: obituaryDetailsInfo.donationRequests,
        notes: obituaryDetailsInfo.notes,
        writerName: obituaryDetailsInfo.writerName,
        relationship: obituaryDetailsInfo.relationship,
        contactPhone: obituaryDetailsInfo.contactPhone,
        contactEmail: obituaryDetailsInfo.contactEmail,
        backupWriter: obituaryDetailsInfo.backupWriter,
        backupContact: obituaryDetailsInfo.backupContact,
        writingStyle: obituaryDetailsInfo.writingStyle,
        collaborationPreference: obituaryDetailsInfo.collaborationPreference,
        deadlinePreference: obituaryDetailsInfo.deadlinePreference,
        sourceMaterials: obituaryDetailsInfo.sourceMaterials,
        interviewContacts: obituaryDetailsInfo.interviewContacts,
        specialInstructions: obituaryDetailsInfo.specialInstructions,
        writerNotes: obituaryDetailsInfo.writerNotes,
      };

      navigation.navigate('ObituaryDetailsInput', {
        obituaryDetailsData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('ObituaryDetailsInput');
    }
  };

  const handleDelete = () => {
    if (obituaryDetailsInfo) {
      Alert.alert(
        'Delete Obituary Details',
        'Are you sure you want to delete this obituary details? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await obituaryDetailsService.deleteObituaryDetails(obituaryDetailsInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if needed
                const remainingEntries = await obituaryDetailsService.getAllObituaryDetails();
                if (remainingEntries.length === 0) {
                  await tasksService.resetTask('obituary_details');
                }

                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting obituary details info:', error);
                Alert.alert('Error', 'Failed to delete obituary details');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Obituary Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Obituary Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {obituaryDetailsInfo ? (
            <>
              {obituaryDetailsInfo.obituaryPreference && (
                <InfoItem label="Obituary Preference" value={obituaryDetailsInfo.obituaryPreference} />
              )}
              {obituaryDetailsInfo.publicationNewspapers && (
                <InfoItem label="Publication Newspapers" value={obituaryDetailsInfo.publicationNewspapers} />
              )}
              {obituaryDetailsInfo.onlinePublications && (
                <InfoItem label="Online Publications" value={obituaryDetailsInfo.onlinePublications} />
              )}
              {obituaryDetailsInfo.lengthPreference && (
                <InfoItem label="Length Preference" value={obituaryDetailsInfo.lengthPreference} />
              )}
              {obituaryDetailsInfo.tonePreference && (
                <InfoItem label="Tone Preference" value={obituaryDetailsInfo.tonePreference} />
              )}
              {obituaryDetailsInfo.keyAccomplishments && (
                <InfoItem label="Key Accomplishments" value={obituaryDetailsInfo.keyAccomplishments} />
              )}
              {obituaryDetailsInfo.familyMentions && (
                <InfoItem label="Family Mentions" value={obituaryDetailsInfo.familyMentions} />
              )}
              {obituaryDetailsInfo.careerHighlights && (
                <InfoItem label="Career Highlights" value={obituaryDetailsInfo.careerHighlights} />
              )}
              {obituaryDetailsInfo.hobbiesInterests && (
                <InfoItem label="Hobbies & Interests" value={obituaryDetailsInfo.hobbiesInterests} />
              )}
              {obituaryDetailsInfo.charitableWork && (
                <InfoItem label="Charitable Work" value={obituaryDetailsInfo.charitableWork} />
              )}
              {obituaryDetailsInfo.militaryService && (
                <InfoItem label="Military Service" value={obituaryDetailsInfo.militaryService} />
              )}
              {obituaryDetailsInfo.educationBackground && (
                <InfoItem label="Education Background" value={obituaryDetailsInfo.educationBackground} />
              )}
              {obituaryDetailsInfo.avoidTopics && (
                <InfoItem label="Topics to Avoid" value={obituaryDetailsInfo.avoidTopics} />
              )}
              {obituaryDetailsInfo.photoPreference && (
                <InfoItem label="Photo Preference" value={obituaryDetailsInfo.photoPreference} />
              )}
              {obituaryDetailsInfo.serviceInformation && (
                <InfoItem label="Service Information" value={obituaryDetailsInfo.serviceInformation} />
              )}
              {obituaryDetailsInfo.donationRequests && (
                <InfoItem label="Donation Requests" value={obituaryDetailsInfo.donationRequests} />
              )}
              {obituaryDetailsInfo.notes && (
                <InfoItem label="Notes" value={obituaryDetailsInfo.notes} />
              )}
              {obituaryDetailsInfo.writerName && (
                <InfoItem label="Writer Name" value={obituaryDetailsInfo.writerName} />
              )}
              {obituaryDetailsInfo.relationship && (
                <InfoItem label="Writer Relationship" value={obituaryDetailsInfo.relationship} />
              )}
              {obituaryDetailsInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={obituaryDetailsInfo.contactPhone} />
              )}
              {obituaryDetailsInfo.contactEmail && (
                <InfoItem label="Contact Email" value={obituaryDetailsInfo.contactEmail} />
              )}
              {obituaryDetailsInfo.backupWriter && (
                <InfoItem label="Backup Writer" value={obituaryDetailsInfo.backupWriter} />
              )}
              {obituaryDetailsInfo.backupContact && (
                <InfoItem label="Backup Contact" value={obituaryDetailsInfo.backupContact} />
              )}
              {obituaryDetailsInfo.writingStyle && (
                <InfoItem label="Writing Style" value={obituaryDetailsInfo.writingStyle} />
              )}
              {obituaryDetailsInfo.collaborationPreference && (
                <InfoItem label="Collaboration Preference" value={obituaryDetailsInfo.collaborationPreference} />
              )}
              {obituaryDetailsInfo.deadlinePreference && (
                <InfoItem label="Deadline Preference" value={obituaryDetailsInfo.deadlinePreference} />
              )}
              {obituaryDetailsInfo.sourceMaterials && (
                <InfoItem label="Source Materials" value={obituaryDetailsInfo.sourceMaterials} />
              )}
              {obituaryDetailsInfo.interviewContacts && (
                <InfoItem label="Interview Contacts" value={obituaryDetailsInfo.interviewContacts} />
              )}
              {obituaryDetailsInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={obituaryDetailsInfo.specialInstructions} />
              )}
              {obituaryDetailsInfo.writerNotes && (
                <InfoItem label="Writer Notes" value={obituaryDetailsInfo.writerNotes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No obituary details found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Obituary Details</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default ObituaryDetailsDataScreen;
